/**
 * Provider card primitives — extract the two patterns that repeat verbatim
 * across the 5 Instant-Pricing provider renderers (photo-provider,
 * bd-provider, bd-real-provider, real-venue, generic provider).
 *
 * Intentionally tiny: each renderer keeps full control over the inline
 * middle content (decor strips, theme grids, photo equip lists, etc.).
 * Centralising more than this couples unrelated UIs and creates a
 * configuration-soup component — exactly the anti-pattern the user wants
 * to avoid.
 */
import React from 'react';

/**
 * The outer card wrapper. Cart-conflict styling is identical in every
 * renderer, so it lives here. Pass `extraClassName` for any
 * renderer-specific tweaks (e.g. compact padding on Birthday cards).
 */
export const ProviderCardFrame = React.forwardRef(function ProviderCardFrame(
  {
    inCart = false,
    testId,
    children,
    className = '',
    /** Override the default border-[#E8E1D5] used when NOT in cart.
     *  Pass e.g. 'border-emerald-200' for the verified-birthday-decorator
     *  card which uses a slightly different palette. */
    notInCartBorderClass = 'border-[#E8E1D5]',
    /** Override the default in-cart ring/border classes. The verified
     *  real-venue uses a slimmer ring than the rest. */
    inCartBorderClass = 'border-green-500 ring-2 ring-green-200',
    as: Tag = 'div',
    ...rest
  },
  ref,
) {
  const base = 'border-2 rounded-lg overflow-hidden transition-all mb-3';
  const cartBorder = inCart ? inCartBorderClass : notInCartBorderClass;
  return (
    <Tag
      ref={ref}
      data-testid={testId}
      className={`${base} ${cartBorder} ${className}`.trim()}
      {...rest}
    >
      {children}
    </Tag>
  );
});

/**
 * The clickable avatar + name + rating + location row that opens the
 * full-screen ProviderShowcaseLightbox.
 *
 * Pass `disabled` for caterer / catering-staff cards where the showcase
 * should not open (those flow through the menu/team-pricing UI instead).
 */
export const ProviderShowcaseHeader = ({
  onOpen,
  disabled = false,
  headerTestId,
  className = '',
  children,
}) => {
  const interactive = !disabled && typeof onOpen === 'function';
  return (
    <div
      className={`flex items-start gap-3 ${interactive ? 'cursor-pointer' : ''} ${className}`.trim()}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onOpen : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } } : undefined}
      data-testid={interactive ? headerTestId : undefined}
    >
      {children}
    </div>
  );
};
